import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnInit } from '@angular/core';
import { CommitmentEvent, MemberCommitment } from 'src/app/shared/models/impact-panel.model';
import { ImpactPanelService } from 'src/app/shared/services/impact-panel.service';
import {
  DetailTimelineRow,
  assignedStatusMeta,
  buildCommitmentTimelineRows,
  buildFallbackTimelineRows,
  docKind,
  fmtDate,
} from 'src/app/shared/utils/impact-panel.utils';

const TIMELINE_PAGE_SIZE = 20;

@Component({
  selector: 'app-commitment-card',
  templateUrl: './commitment-card.component.html',
  styleUrls: ['./commitment-card.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CommitmentCardComponent implements OnInit {
  @Input() commitment!: MemberCommitment;
  @Input() walletAddress!: string;

  expanded = false;
  loadingDetail = false;
  detailLoaded = false;
  timelineRows: DetailTimelineRow[] = [];
  documents: any[] = [];
  hasMoreEvents = false;
  loadingMore = false;

  private rawEvents: CommitmentEvent[] = [];
  private offset = 0;

  constructor(
    private impactPanelService: ImpactPanelService,
    private cdr: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.timelineRows = buildFallbackTimelineRows(this.commitment);
  }

  get isRemoved(): boolean {
    return !!this.commitment.removalDate;
  }

  get dotClass(): string {
    if (this.isRemoved) return 'def';
    return this.commitment.assignedStatusCode === 2
      ? 'suc'
      : this.commitment.assignedStatusCode === 1
        ? 'warn'
        : 'def';
  }

  get assignedStatus() {
    return assignedStatusMeta(this.commitment.assignedStatus);
  }

  get esgActive(): string[] {
    return (this.commitment.ESG || [])
      .map((active, idx) => (active ? ['E', 'S', 'G'][idx] : null))
      .filter((v): v is string => !!v);
  }

  formatDate(iso?: string | null): string {
    return fmtDate(iso);
  }

  rowTone(cssClass: string): string {
    if (cssClass === 'created' || cssClass === 'enabled') return 'suc';
    if (cssClass === 'deleted') return 'err';
    if (cssClass === 'disabled') return 'def';
    return 'info';
  }

  trackByRow(_index: number, row: DetailTimelineRow): string {
    return `${row.isoDate}-${row.label}-${row.txHash || ''}`;
  }

  docKindOf(mimeType?: string): 'image' | 'pdf' | 'other' {
    return docKind(mimeType);
  }

  toggle(): void {
    this.expanded = !this.expanded;
    if (this.expanded && !this.detailLoaded && !this.loadingDetail) {
      this.loadDetail();
    }
  }

  loadMore(): void {
    if (!this.hasMoreEvents || this.loadingMore) return;
    this.loadingMore = true;
    this.impactPanelService
      .getCommitmentTimeline(this.walletAddress, this.commitment.code, TIMELINE_PAGE_SIZE, this.offset)
      .subscribe({
        next: detail => {
          this.rawEvents = [...this.rawEvents, ...(detail.events || [])];
          this.timelineRows = buildCommitmentTimelineRows(this.rawEvents);
          this.documents = [...this.documents, ...(detail.documents || [])];
          this.offset += (detail.events || []).length;
          this.hasMoreEvents = !!detail.hasMore;
          this.loadingMore = false;
          this.cdr.markForCheck();
        },
        // No se apaga hasMoreEvents en error, para permitir reintentar el "Cargar más".
        error: () => {
          this.loadingMore = false;
          this.cdr.markForCheck();
        },
      });
  }

  private loadDetail(): void {
    this.loadingDetail = true;
    this.impactPanelService
      .getCommitmentTimeline(this.walletAddress, this.commitment.code, TIMELINE_PAGE_SIZE, 0)
      .subscribe({
        next: detail => {
          // Segunda emisión (refresco tras el cache): si ya tocó "Cargar más", se descarta para
          // no perder los eventos agregados.
          if (
            this.detailLoaded &&
            (this.loadingMore || this.rawEvents.length > (detail.events || []).length)
          ) {
            return;
          }
          this.rawEvents = detail.events || [];
          this.timelineRows = buildCommitmentTimelineRows(this.rawEvents);
          this.documents = detail.documents || [];
          this.offset = this.rawEvents.length;
          this.hasMoreEvents = !!detail.hasMore;
          this.detailLoaded = true;
          this.loadingDetail = false;
          this.cdr.markForCheck();
        },
        error: () => {
          // Se conserva el timeline de respaldo armado en ngOnInit; no se reintenta para
          // no golpear el endpoint en cada expand/collapse.
          this.detailLoaded = true;
          this.loadingDetail = false;
          this.cdr.markForCheck();
        },
      });
  }
}
